import { useMemo, useReducer, useRef } from 'react';
import { serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { addingStepChangesLockedStageOrder, reorderChangesLockedStage, reorderChangesLockedStep, stepHasLockedApprover } from '../utils/approvalStepsUtils';
const EMPTY_LOCKED_ACTOR_KEYS = new Set();
const EMPTY_LOCKED_STAGE_INDEXES = new Set();
const makeStep = (seq, approvers = [], templateStageLocked = false) => ({
  id: `step-${seq}`,
  approvers,
  templateStageLocked
});
export const initApprovalStepsState = (groups, startSeq = 0, lockedStageIndexes = EMPTY_LOCKED_STAGE_INDEXES) => {
  if (groups.length === 0) {
    return {
      steps: [makeStep(startSeq)],
      seq: startSeq + 1
    };
  }
  return {
    steps: groups.map((approvers, index) => makeStep(startSeq + index, approvers, lockedStageIndexes.has(index))),
    seq: startSeq + groups.length
  };
};
const moveInArray = (arr, fromIndex, toIndex) => {
  const next = [...arr];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};
export const approvalStepsReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_STEP':
      {
        const steps = [...state.steps];
        const atIndex = action.atIndex === undefined ? steps.length : Math.min(Math.max(action.atIndex, 0), steps.length);
        steps.splice(atIndex, 0, makeStep(state.seq));
        return {
          steps,
          seq: state.seq + 1
        };
      }
    case 'REMOVE_STEP':
      return Object.assign({}, state, {
        steps: state.steps.filter(step => step.id !== action.stepId)
      });
    case 'REORDER_STEPS':
      return Object.assign({}, state, {
        steps: moveInArray(state.steps, action.fromIndex, action.toIndex)
      });
    case 'ADD_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId && !step.approvers.some(approver => serializeActor(approver.actor) === serializeActor(action.approver.actor)) ? Object.assign({}, step, {
          approvers: [...step.approvers, action.approver]
        }) : step)
      });
    case 'REMOVE_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId ? Object.assign({}, step, {
          approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
        }) : step)
      });
    case 'MOVE_APPROVER':
      {
        let moved;
        const withoutApprover = state.steps.map(step => {
          const found = step.approvers.find(approver => serializeActor(approver.actor) === action.actorKey);
          if (!found) {
            return step;
          }
          moved = found;
          return Object.assign({}, step, {
            approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
          });
        });
        if (!moved) {
          return state;
        }
        const movedApprover = moved;
        return Object.assign({}, state, {
          steps: withoutApprover.map(step => {
            if (step.id !== action.toStepId) {
              return step;
            }
            const approvers = [...step.approvers];
            const insertIndex = action.toIndex === undefined ? approvers.length : Math.min(Math.max(action.toIndex, 0), approvers.length);
            approvers.splice(insertIndex, 0, movedApprover);
            return Object.assign({}, step, {
              approvers
            });
          })
        });
      }
    case 'INIT':
      return initApprovalStepsState(action.groups, state.seq);
    default:
      return state;
  }
};
export const useApprovalSteps = (initialGroups = [], lockedActorKeys = EMPTY_LOCKED_ACTOR_KEYS, lockedStageIndexes = EMPTY_LOCKED_STAGE_INDEXES, stepListLocked = false) => {
  const [state, dispatch] = useReducer(approvalStepsReducer, initialGroups, groups => initApprovalStepsState(groups, 0, lockedStageIndexes));

  // Already-approved approvers (and the steps containing them) are locked: they
  // can't be removed, moved, or have their step order changed. The latest set
  // and state are read through refs so the guards stay correct as
  // approvedApproverActorKeys loads, while keeping the action creators stable.
  const lockedActorKeysRef = useRef(lockedActorKeys);
  lockedActorKeysRef.current = lockedActorKeys;
  const stepListLockedRef = useRef(stepListLocked);
  stepListLockedRef.current = stepListLocked;
  const stateRef = useRef(state);
  stateRef.current = state;
  const actions = useMemo(() => ({
    addStep: atIndex => {
      if (stepListLockedRef.current) {
        return;
      }
      const insertionIndex = atIndex !== null && atIndex !== void 0 ? atIndex : stateRef.current.steps.length;
      if (addingStepChangesLockedStageOrder(stateRef.current.steps, insertionIndex)) {
        return;
      }
      dispatch({
        type: 'ADD_STEP',
        atIndex
      });
    },
    removeStep: stepId => {
      if (stepListLockedRef.current) {
        return;
      }
      const step = stateRef.current.steps.find(s => s.id === stepId);
      if (step && (step.templateStageLocked || stepHasLockedApprover(step, lockedActorKeysRef.current))) {
        return;
      }
      dispatch({
        type: 'REMOVE_STEP',
        stepId
      });
    },
    reorderSteps: (fromIndex, toIndex) => {
      if (stepListLockedRef.current || reorderChangesLockedStep(stateRef.current.steps, fromIndex, toIndex, lockedActorKeysRef.current) || reorderChangesLockedStage(stateRef.current.steps, fromIndex, toIndex)) {
        return;
      }
      dispatch({
        type: 'REORDER_STEPS',
        fromIndex,
        toIndex
      });
    },
    addApprover: (stepId, approver) => {
      var _stateRef$current$ste;
      if (stepListLockedRef.current || (_stateRef$current$ste = stateRef.current.steps.find(step => step.id === stepId)) !== null && _stateRef$current$ste !== void 0 && _stateRef$current$ste.templateStageLocked) {
        return;
      }
      dispatch({
        type: 'ADD_APPROVER',
        stepId,
        approver
      });
    },
    removeApprover: (stepId, actorKey) => {
      var _stateRef$current$ste2;
      if (stepListLockedRef.current || lockedActorKeysRef.current.has(actorKey) || (_stateRef$current$ste2 = stateRef.current.steps.find(step => step.id === stepId)) !== null && _stateRef$current$ste2 !== void 0 && _stateRef$current$ste2.templateStageLocked) {
        return;
      }
      dispatch({
        type: 'REMOVE_APPROVER',
        stepId,
        actorKey
      });
    },
    moveApprover: args => {
      const sourceStep = stateRef.current.steps.find(step => step.approvers.some(approver => serializeActor(approver.actor) === args.actorKey));
      const destinationStep = stateRef.current.steps.find(step => step.id === args.toStepId);
      if (stepListLockedRef.current || lockedActorKeysRef.current.has(args.actorKey) || sourceStep !== null && sourceStep !== void 0 && sourceStep.templateStageLocked || destinationStep !== null && destinationStep !== void 0 && destinationStep.templateStageLocked) {
        return;
      }
      dispatch(Object.assign({
        type: 'MOVE_APPROVER'
      }, args));
    },
    init: groups => dispatch({
      type: 'INIT',
      groups
    })
  }), []);
  return Object.assign({
    steps: state.steps
  }, actions);
};